import React, { useEffect, useId, useMemo, useRef } from "react"
import cx from "classnames"
import { animate, motion, useMotionValue, type PanInfo } from "framer-motion"
import { Popover } from "@base-ui/react/popover"
import {
  NavigationFlyout,
  NavigationFlyoutProvider,
  type NavigationFlyoutHandle,
  type NavigationFlyoutPayload
} from "./navigation-flyout"
import { useNavigationContext } from "./navigation-context"
import {
  COLLAPSED_WIDTH_DESKTOP,
  EXPANDED_WIDTH,
  EXPANDED_WIDTH_PX,
  EXPANDED_CONTENT_WIDTH,
  isInteractiveElement
} from "./navigation-utils"
import { VisuallyHidden } from "../visually-hidden"

// =============================================================================
// Props
// =============================================================================

export type NavigationSidebarProps = {
  /**
   * Accessible name for the nav landmark. Defaults to "Main".
   * Assistive technologies announce the landmark role automatically —
   * omit "navigation" from the name. Override when a page has more than one `Navigation`.
   */
  accessibleName?: string
  children?: React.ReactNode
  /** Additional CSS classes */
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const navigationStyles = [
  // Drives the collapsed logo→icon swap on hover and keyboard focus.
  "group/sidebar",
  "flex h-full flex-col",
  "pb-2",
  "shrink-0 overflow-hidden",
  "bg-warm-white-100"
].join(" ")

// Mobile: fixed width, slides in/out via margin-left so the sidebar stays in flow and pushes content.
// The animation is driven by a motion value (not a CSS transition) so pan gestures can scrub it.
const mobileNavigationStyles = [
  "w-(--navigation-sidebar-width)",
  "select-none"
].join(" ")

// Desktop: width-based collapse, stays in document flow
const desktopNavigationStyles = "w-(--navigation-width)"

// Animate the width change of an in-place collapse/expand on desktop. Suppressed while crossing
// the mobile breakpoint, so the sidebar snaps to its desktop width instead of sliding in from
// mobile's wider 256px
const desktopWidthTransition = "transition-[width] duration-[250ms] ease-out"

function replacesCurrentPage(event: React.MouseEvent<HTMLElement>): boolean {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
    return false
  const link = (event.target as HTMLElement).closest("a[href]")
  return link !== null && link.getAttribute("target") !== "_blank"
}

// Dead zone before a pan starts moving the sidebar — filters accidental touches
// and gives the user a chance to tap children without dragging.
const PAN_ACTIVATION_PX = 24
// Close if dragged past this distance OR flicked at this velocity
const SWIPE_CLOSE_DISTANCE_PX = 80
const SWIPE_CLOSE_VELOCITY_PX_S = 500
const SLIDE_DURATION_S = 0.25

// =============================================================================
// Component
// =============================================================================

/**
 * The sidebar nav element. Must be rendered inside `Navigation`.
 * Renders the `<nav>` landmark, manages the flyout panel for collapsed groups,
 * and handles mobile vs desktop width behavior.
 *
 * @example
 * <Navigation currentPath={currentPath}>
 *   <Navigation.Sidebar>
 *     <Navigation.Header>...</Navigation.Header>
 *     <Navigation.Menu>...</Navigation.Menu>
 *   </Navigation.Sidebar>
 * </Navigation>
 */
export function NavigationSidebar({
  accessibleName = "Main",
  children,
  className
}: NavigationSidebarProps) {
  const { collapsed, mobile, setCollapsed } = useNavigationContext()
  const accessibleNameId = useId()

  // One handle per Navigation instance. Each collapsed Navigation.Group
  // registers itself as a Popover.Trigger against this handle so they all
  // share the single flyout panel rendered below.
  const flyoutHandle = useMemo<NavigationFlyoutHandle>(
    () => Popover.createHandle<NavigationFlyoutPayload>(),
    []
  )

  // Resting margin-left: desktop never offsets the sidebar (collapse is width-based) so it rests at
  // 0; mobile hides it fully off-screen when collapsed.
  const restingMargin = mobile && collapsed ? -EXPANDED_WIDTH_PX : 0

  // Drive marginLeft via a motion value so pan gestures can scrub it directly (instead of `drag="x"`
  // which animates transform — transform doesn't reflow content). Single source of truth for the
  // sidebar's margin-left in both modes.
  const marginLeft = useMotionValue(restingMargin)
  // Previous `mobile`, committed in an effect (not during render) so `crossedBreakpoint` derives the
  // same value on every render pass. Lets us tell a breakpoint cross apart from a collapse toggle.
  const wasMobile = useRef(mobile)
  // Captured marginLeft at the moment the pan crosses the activation threshold.
  // `null` means the current gesture hasn't activated yet (or no gesture in progress).
  const panOriginMargin = useRef<number | null>(null)

  // Crossing the mobile breakpoint snaps the sidebar to its resting position with no animation, and
  // suppresses the desktop width transition below so the width snaps too. The `jump` clears the
  // stale off-screen margin left over from mobile when returning to desktop. `crossedBreakpoint`
  // derives from a ref committed in the same effect, so it's stable across render passes (and gates
  // the width transition below).
  const crossedBreakpoint = wasMobile.current !== mobile
  useEffect(() => {
    if (crossedBreakpoint) {
      marginLeft.jump(restingMargin)
    }
    wasMobile.current = mobile
  }, [crossedBreakpoint, mobile, marginLeft, restingMargin])

  // Animate the slide only for in-place collapse toggles while on mobile (dock trigger, overlay
  // tap, swipe-to-close). Breakpoint crosses are handled by the snap above, and desktop has no
  // margin offset to animate.
  useEffect(() => {
    if (!mobile) return
    const controls = animate(marginLeft, restingMargin, {
      duration: SLIDE_DURATION_S,
      ease: "easeOut"
    })
    return () => controls.stop()
  }, [mobile, marginLeft, restingMargin])

  function handlePanStart() {
    panOriginMargin.current = null
  }

  function handlePan(_: PointerEvent, info: PanInfo) {
    if (panOriginMargin.current === null) {
      if (info.offset.x > -PAN_ACTIVATION_PX) return
      panOriginMargin.current = marginLeft.get()
    }
    // Subtract the activation distance so the sidebar starts moving from where the finger
    // is *now*, not from where it crossed the threshold — otherwise it would jump.
    const next = panOriginMargin.current + info.offset.x + PAN_ACTIVATION_PX
    marginLeft.set(Math.min(0, Math.max(-EXPANDED_WIDTH_PX, next)))
  }

  function handlePanEnd(_: PointerEvent, info: PanInfo) {
    const shouldClose =
      info.offset.x < -SWIPE_CLOSE_DISTANCE_PX ||
      info.velocity.x < -SWIPE_CLOSE_VELOCITY_PX_S
    if (shouldClose) {
      setCollapsed(true)
    } else {
      // Always settle at 0 (the canonical open resting position) rather than the value at
      // pan-start — guarantees a clean snap-back even if the pan began mid-animation.
      animate(marginLeft, 0, {
        duration: SLIDE_DURATION_S,
        ease: "easeOut"
      })
    }
  }

  const panEnabled = mobile && !collapsed

  function handleNavClick(event: React.MouseEvent<HTMLElement>) {
    // Ignore clicks bubbling in from portalled content (e.g. the trial
    // indicator's hover card)
    if (!event.currentTarget.contains(event.target as Node)) return
    if (mobile) {
      // Collapse the navigation when clicking on a navigation link
      if (replacesCurrentPage(event)) setCollapsed(true)
      return
    }
    if (!collapsed) return
    // Leave clicks on interactive elements (e.g. nav links) to their own behavior.
    if (isInteractiveElement(event.target as HTMLElement)) return
    setCollapsed(false)
  }

  return (
    <NavigationFlyoutProvider value={flyoutHandle}>
      <motion.nav
        data-slot="navigation"
        data-state={collapsed ? "collapsed" : "expanded"}
        onPanStart={panEnabled ? handlePanStart : undefined}
        onPan={panEnabled ? handlePan : undefined}
        onPanEnd={panEnabled ? handlePanEnd : undefined}
        onClick={handleNavClick}
        style={
          (mobile
            ? {
                "--navigation-sidebar-width": EXPANDED_WIDTH,
                "--navigation-expanded-content-width": EXPANDED_CONTENT_WIDTH,
                marginLeft
              }
            : {
                "--navigation-width": collapsed
                  ? COLLAPSED_WIDTH_DESKTOP
                  : EXPANDED_WIDTH,
                "--navigation-expanded-content-width": EXPANDED_CONTENT_WIDTH,
                // Bind the same motion value desktop pins at 0, so the offset mobile wrote to the
                // DOM is always overwritten on this side — never left stale.
                marginLeft
              }) as unknown as React.CSSProperties
        }
        className={cx(
          navigationStyles,
          mobile ? mobileNavigationStyles : desktopNavigationStyles,
          !mobile && !crossedBreakpoint && desktopWidthTransition,
          !mobile && collapsed && "cursor-pointer",
          className
        )}
        aria-labelledby={accessibleNameId}
        {...(mobile && collapsed ? { inert: "" } : {})}
      >
        <VisuallyHidden id={accessibleNameId}>{accessibleName}</VisuallyHidden>
        {children}
      </motion.nav>

      <NavigationFlyout handle={flyoutHandle} />
    </NavigationFlyoutProvider>
  )
}

NavigationSidebar.displayName = "Navigation.Sidebar"
